import { Platform } from 'react-native';
import { CommonActions, StackActions } from '@react-navigation/native';
import { HIDE_LOADING_MODAL, SHOW_AUTH_MODAL, SHOW_LOADING_MODAL, SHOW_OTP, SHOW_TC_MODAL, SUCCESS, INITIALISE_MENU, TOGGLE_EASY_MODE, START_ONBOARDING, FORCE_FOCUS_MODE, TOGGLE_FOCUS_MODE, SET_SKIP_NATIVE_LOGIN_SESSION_FETCH } from '@reduxLocal/actions/types';
import getTCConfirm from '@components/modals/TCModal/utils/getTCConfirm';
import apis from '@api/api';
import DeviceInfo from 'react-native-device-info';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { store } from '@reduxLocal/persistState';
import { navigationRef } from '@navigation/rootNavigation';
import { markRecentAuthSuccess } from '@utils/authGracePeriod';
import { computeSessionExpireAt } from '@utils/sessionConstants';
import { persistAuthSession } from '@utils/persistentSessionStorage';
import { beginLoginArtDim } from '@navigation/loginArt/loginArtTransition';
import { beginLoginGlassSplit } from '@navigation/loginArt/loginGlassSplitTransition';
import { syncWatchSessionLater } from '@services/watchSessionSync';
import { trackSignupComplete } from '@utils/conversionAnalytics';
import {
  consumePendingDashboardRoute,
  type PendingDashboardRoute,
} from '@navigation/LinkingConfiguration';
import { clearPendingDashboardDeepLink } from '@navigation/utils/pendingDashboardDeepLink';
import {
  consumePendingEasyModeDeepLink,
  hasPendingEasyModePrompt,
} from '@navigation/screens/MidLevel/EasyModeScreen/utils/easyModeDeepLink';
import { consumePendingPreviewId } from '@navigation/screens/TopLevel/PreviewScreen/utils/pendingPreview';

/**
 * Prefer root navigation ref on native so OAuth completes even when the hook `navigation`
 * from a child button does not control the Login stack (common after returning from the system browser).
 */
/**
 * Same “new user → Easy Mode onboarding” rule as post-OAuth navigation.
 * Exported so Apple (and other non-handleOAuth paths) stay aligned.
 */
export function computePostOAuthIsNewUser(amendedData: any): boolean {
  const tutorialModal = amendedData?.userData?.tutorials?.tutorialModal;
  const userData = amendedData?.userData;

  const hasCompany = amendedData?.company || userData?.currentCompany;
  const hasIntegrations =
    userData?.integrations && Object.keys(userData.integrations).length > 0;
  const hasLoginHistory = userData?.login_history?.previous_logins?.length > 0;
  const hasStickyNotes = userData?.sticky_notes?.length > 0;
  const hasUserInitialised = userData?.user_initialised === true;

  const hasAnyUserActivity =
    hasCompany ||
    hasIntegrations ||
    hasLoginHistory ||
    hasStickyNotes ||
    hasUserInitialised;

  return tutorialModal === false && !hasAnyUserActivity;
}

/**
 * Enable Easy Mode + onboarding without flipping easyMode off if already on.
 * A pending business prompt (marketing `?prompt=`) skips onboarding: the onboarding chat
 * would swallow the auto-submitted prompt and wipe it on completion.
 */
export function dispatchPostAuthEasyModeOnboarding(dispatch: (action: any) => void) {
  const ui = store.getState()?.UI;
  if (!ui?.easyMode) {
    dispatch({ type: TOGGLE_EASY_MODE });
  }
  if (!ui?.onboardingInProgress && !hasPendingEasyModePrompt()) {
    dispatch({ type: START_ONBOARDING });
  }
}

export type RootDashboardParams = {
  screen?: 'Welcome' | 'EasyMode';
  params?: {
    session?: unknown;
    initialUserPrompt?: string;
    initialPromptNonce?: number;
    previewId?: string;
  };
};

/**
 * OAuth can reach navigateToRootDashboard twice (session effect + OAuth callback). React Navigation 6
 * replaces nested params, so a second EasyMode navigate would strip the just-applied prompt.
 */
const PENDING_PROMPT_SETTLE_MS = 5000;
let pendingPromptAppliedAt = 0;

/** Consume a stashed Easy Mode prompt (one-shot) and fold it into the Dashboard params. */
function applyPendingEasyModePrompt(
  nested?: RootDashboardParams
): { nested?: RootDashboardParams; applied: boolean } {
  const pending = consumePendingEasyModeDeepLink();
  if (!pending) return { nested, applied: false };
  pendingPromptAppliedAt = Date.now();
  const prompt = pending.initialUserPrompt?.trim();
  const previewId = pending.source === 'marketing' ? consumePendingPreviewId() : undefined;
  return {
    applied: true,
    nested: {
      screen: 'EasyMode',
      params: {
        ...(nested?.screen === 'EasyMode' ? nested.params : undefined),
        ...(prompt
          ? { initialUserPrompt: prompt, initialPromptNonce: pending.initialPromptNonce ?? Date.now() }
          : {}),
        ...(previewId ? { previewId } : {}),
      },
    },
  };
}

/** True when the app root navigator is showing Dashboard (web root stack or native Login stack). */
export function isAppOnDashboard(): boolean {
  if (!navigationRef.isReady()) return false;
  try {
    const root = navigationRef.getRootState();
    const top = root?.routes?.[root?.index ?? 0];
    return top?.name === 'Dashboard';
  } catch {
    return false;
  }
}

/**
 * Navigate to Dashboard on the root navigator. Required on web where Login lives under Auth,
 * not as a sibling of Dashboard.
 */
export function navigateToRootDashboard(requested?: RootDashboardParams): void {
  if (!navigationRef.isReady()) return;

  const { nested, applied: pendingPromptApplied } = applyPendingEasyModePrompt(requested);

  if (isAppOnDashboard()) {
    const promptJustApplied =
      !pendingPromptApplied && Date.now() - pendingPromptAppliedAt < PENDING_PROMPT_SETTLE_MS;
    if (nested?.screen && !promptJustApplied) {
      (navigationRef as { navigate: (name: string, params: object) => void }).navigate(
        'Dashboard',
        nested
      );
    }
    return;
  }

  if (Platform.OS === 'web') {
    // Curtain: keep shell art mounted and dim while Dashboard resets underneath.
    beginLoginArtDim();
    // Optional morph (web/Electron): login glass → menu / messages / footer.
    // Disable via loginGlassSplitConfig.ts or EXPO_PUBLIC_LOGIN_GLASS_SPLIT=0.
    beginLoginGlassSplit(!!store.getState()?.app?.theme?.lightTheme);
    // Return to the dashboard URL that sent the user to login, unless onboarding or a pending
    // Easy Mode prompt owns the route.
    let pendingDashboard: PendingDashboardRoute | null = null;
    if (pendingPromptApplied || store.getState()?.UI?.onboardingInProgress) {
      clearPendingDashboardDeepLink();
    } else {
      pendingDashboard = consumePendingDashboardRoute();
    }
    navigationRef.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [
          pendingDashboard?.kind === 'route'
            ? pendingDashboard.route
            : {
                name: 'Dashboard',
                params: nested?.screen ? nested : undefined,
              },
        ],
      })
    );
    return;
  }

  navigationRef.dispatch(StackActions.replace('Dashboard', nested));
}

export function replaceLoginStackWithDashboard(
  navigation: any,
  isNewUser: boolean,
  amendedData: any
) {
  const nestedParams: RootDashboardParams = { screen: 'EasyMode', params: { session: amendedData } };

  markRecentAuthSuccess();

  if (navigationRef.isReady()) {
    navigateToRootDashboard(nestedParams);
    return;
  }

  if (navigation?.replace) {
    try {
      const state = navigation.getState?.();
      const idx = state?.index ?? 0;
      if (state?.routes?.[idx]?.name === 'Dashboard') {
        navigation.navigate?.('Dashboard', nestedParams);
        return;
      }
    } catch (_) {
      /* ignore */
    }
    navigation.replace('Dashboard', nestedParams);
  } else {
    console.warn('[authUtils] No navigation available for post-OAuth dashboard');
  }
}

/**
 * After sign-up (email plan selection, WhatsApp register, invite password): persist session,
 * mark authenticated, enable onboarding, route to EasyMode.
 * Always treats the user as new (registration login_history would otherwise skip onboarding).
 * Must dispatch SUCCESS before Dashboard mount — DashStack redirects to Login when !isAuthenticated.
 */
export async function navigatePostEmailSignupToEasyMode(
  dispatch: (action: any) => void,
  navigation: any,
  amendedData: any,
  options?: { selectedPlan?: unknown; signupMethod?: string }
) {
  if (!amendedData?.userData?.id) {
    console.error('[authUtils] navigatePostEmailSignupToEasyMode: missing user id');
    return;
  }

  const sessionPayload = {
    ...amendedData,
    rememberMe: true,
    expireAt: amendedData.expireAt ?? computeSessionExpireAt(true),
    ...(options?.selectedPlan != null ? { selectedPlan: options.selectedPlan } : {}),
  };

  try {
    await persistAuthSession({
      rememberMe: true,
      userId: amendedData?.userData?.id,
      sessionData: sessionPayload,
    });
  } catch (storageError) {
    console.warn('[authUtils] Failed to save post-signup session:', storageError);
  }

  // Required for Dashboard/EasyMode: sets promiseData.isAuthenticated + userData.
  // Email/invite paths often already SUCCESS'd via loginUser; re-dispatch is idempotent.
  dispatch({ type: SUCCESS, data: sessionPayload });

  if (Platform.OS !== 'web') {
    // Avoid LoginScreen fetchAndValidateSession racing Welcome vs EasyMode after root replace.
    dispatch({ type: SET_SKIP_NATIVE_LOGIN_SESSION_FETCH, payload: true });
    syncWatchSessionLater({
      userId: amendedData.userData.id,
      companyId: amendedData?.company?._id || amendedData?.company?.id || null,
      companyName:
        amendedData?.company?.company_name ||
        amendedData?.company?.copmany_name ||
        null,
      targetUserId:
        amendedData?.targetUser?._id || amendedData?.targetUser?.id || null,
    });
  }

  if (Platform.OS === 'web') {
    dispatch({ type: INITIALISE_MENU });
  }

  dispatchPostAuthEasyModeOnboarding(dispatch);
  void trackSignupComplete({
    method: options?.signupMethod || 'email',
    source: 'registration',
  });
  replaceLoginStackWithDashboard(navigation, true, sessionPayload);
}

/**
 * Common authentication utilities shared between different OAuth providers
 */

// Centralized error handler
export function handleError(dispatch: any, message: string) {
  console.error(message);
  setTimeout(() => dispatch({
    type: SHOW_AUTH_MODAL,
    data: { content: message, type: 'error' }
  }), 1000);
}

// Helper function to process user data and handle notification tokens
export async function handleUserData(res: any, notificationToken: string) {
  let amendedData = res.data.data;

  if (Platform.OS !== 'web') {
    let activeTokens = amendedData.notification_info?.map((item: any) => item.notification_token) || [];

    if (!activeTokens.length || activeTokens.includes(notificationToken)) {
      await apis.updateNotificationToken(notificationToken, amendedData.userData.id);
    }

    amendedData.rememberMe = true;
    amendedData.expireAt = computeSessionExpireAt(true);
  }
  return amendedData;
}

// Helper function to handle Terms & Conditions
export async function handleTermsAndConditions(res: any, dispatch: any) {
  if (res.data.terms) {
    dispatch({ type: HIDE_LOADING_MODAL });
    setTimeout(() => dispatch({ type: SHOW_TC_MODAL, payload: res.data.data.userData.id }), 1000);

    try {
      await getTCConfirm();
    } catch (e) {
      // Check if user explicitly declined (intentional cancel)
      const tcModalState = store.getState()?.modals?.tcModal;
      const wasExplicitlyDeclined = tcModalState?.accepted === false;

      if (wasExplicitlyDeclined) {
        console.log('[authUtils] User declined TC modal in handleTermsAndConditions, throwing cancellation error');
        // User cancelled - throw error to prevent handleOAuthResponse from continuing
        throw new Error('TC_MODAL_CANCELLED');
      }

      // Real error (timeout, etc.) - show error message and throw
      console.error('[authUtils] Error awaiting T&C confirmation', e);
      handleError(dispatch, "Could not fetch terms and conditions, please try again");
      throw e; // Re-throw to prevent handleOAuthResponse from continuing
    }
  }
}

// Common OAuth response handler for different providers
export async function handleOAuthResponse(
  res: any,
  dispatch: any,
  navigation: any,
  notificationToken: string,
  providerName: string
) {
  try {
    // MFA check and Terms & Conditions handling
    if (res?.status === 202) {
      dispatch({ type: HIDE_LOADING_MODAL });
      if (res.data.terms) {
        console.log('[authUtils] Status 202 with terms, showing TC modal for userId:', res.data.userId);
        // Clear any cancellation flags before showing modal (user is trying again)
        try {
          const cancelledData = await AsyncStorage.getItem('tc_cancelled');
          if (cancelledData) {
            const { userId: cancelledUserId, timestamp } = JSON.parse(cancelledData);
            // Clear if it's for the same user (they're trying again) or different user
            // Also clear if it's old (more than 30 seconds)
            const isOld = Date.now() - timestamp > 30000;
            if (cancelledUserId === res.data.userId || cancelledUserId !== res.data.userId || isOld) {
              await AsyncStorage.removeItem('tc_cancelled');
              console.log('[authUtils] Cleared cancellation flag before showing modal:', {
                cancelledUserId,
                currentUserId: res.data.userId,
                isOld,
                reason: cancelledUserId === res.data.userId ? 'same user retry' : cancelledUserId !== res.data.userId ? 'different user' : 'old flag'
              });
            }
          }
        } catch (e) {
          console.error('[authUtils] Error checking cancellation flag before showing modal:', e);
        }

        console.log('[authUtils] Dispatching SHOW_TC_MODAL with userId:', res.data.userId);
        dispatch({ type: SHOW_TC_MODAL, payload: res.data.userId });
        try {
          await getTCConfirm();
          // Clear cancellation flag on successful acceptance
          await AsyncStorage.removeItem('tc_cancelled');
          console.log('[authUtils] Terms confirmed, cleared cancellation flag');
          dispatch({ type: SHOW_OTP, payload: res.data });
        } catch (e) {
          // Check if user explicitly declined (intentional cancel)
          const tcModalState = store.getState()?.modals?.tcModal;
          const wasExplicitlyDeclined = tcModalState?.accepted === false;

          if (wasExplicitlyDeclined) {
            console.log('[authUtils] User declined TC modal, not showing error');
            // User cancelled - state is already cleared, no need to show error
            return;
          }

          // Real error (timeout, etc.) - show error message
          console.error('[authUtils] Error in getTCConfirm:', e);
          handleError(dispatch, 'Please accept our terms of service to continue');
        }
      } else {
        dispatch({ type: SHOW_OTP, payload: res.data });
      }
    } else {
      // Check if TC was cancelled by checking AsyncStorage flag
      let tcWasCancelled = false;
      try {
        const cancelledData = await AsyncStorage.getItem('tc_cancelled');
        if (cancelledData) {
          const { userId: cancelledUserId, timestamp } = JSON.parse(cancelledData);
          // Check if cancellation was recent (within last 30 seconds) and for this user
          const isRecent = Date.now() - timestamp < 30000;
          const isSameUser = res?.data?.userId === cancelledUserId || res?.data?.data?.userData?.id === cancelledUserId;
          tcWasCancelled = isRecent && isSameUser;
          console.log('[authUtils] Checking cancellation flag:', {
            cancelledUserId,
            currentUserId: res?.data?.userId || res?.data?.data?.userData?.id,
            isRecent,
            isSameUser,
            tcWasCancelled
          });

          // Clear the flag if it's old
          if (!isRecent) {
            await AsyncStorage.removeItem('tc_cancelled');
          }
        }
      } catch (e) {
        console.error('[authUtils] Error checking cancellation flag:', e);
      }

      if (tcWasCancelled) {
        console.log('[authUtils] TC was cancelled, preventing SUCCESS dispatch and clearing flag');
        await AsyncStorage.removeItem('tc_cancelled');
        return;
      }

      // Only check user data if we have a cancellation flag - this prevents blocking new users
      // New users won't have user data in Redux, so we can't use that as an indicator
      // We rely on the cancellation flag check above instead
      console.log('[authUtils] No cancellation flag found, proceeding with SUCCESS dispatch');

      // Check if TC modal was cancelled before processing success
      const tcModalState = store.getState()?.modals?.tcModal;
      const wasCancelled = tcModalState?.userId && !tcModalState?.modalDisplayed && !tcModalState?.accepted;
      console.log('[authUtils] Checking if TC was cancelled:', {
        userId: tcModalState?.userId,
        modalDisplayed: tcModalState?.modalDisplayed,
        accepted: tcModalState?.accepted,
        wasCancelled
      });

      if (wasCancelled) {
        console.log('[authUtils] TC modal was cancelled, preventing SUCCESS dispatch');
        return;
      }

      // Track if terms were just accepted to allow navigation
      let termsJustAccepted = false;
      
      try {
        await handleTermsAndConditions(res, dispatch);
        // If handleTermsAndConditions completes without error, terms were accepted
        termsJustAccepted = true;
        // Wait a bit for modal state to update after acceptance
        await new Promise(resolve => setTimeout(resolve, 200));
      } catch (e) {
        // Check if cancellation error from handleTermsAndConditions
        if (e?.message === 'TC_MODAL_CANCELLED') {
          console.log('[authUtils] TC modal was cancelled in handleTermsAndConditions, preventing SUCCESS dispatch');
          return;
        }
        // For other errors (timeout, etc.), also prevent SUCCESS dispatch
        console.log('[authUtils] Error in handleTermsAndConditions, preventing SUCCESS dispatch:', e);
        return;
      }

      // Re-check cancellation flag after handleTermsAndConditions (safety check)
      let tcWasCancelledAfter = false;
      try {
        const cancelledDataAfter = await AsyncStorage.getItem('tc_cancelled');
        if (cancelledDataAfter) {
          const { userId: cancelledUserId, timestamp } = JSON.parse(cancelledDataAfter);
          const isRecent = Date.now() - timestamp < 30000;
          const isSameUser = res?.data?.userId === cancelledUserId || res?.data?.data?.userData?.id === cancelledUserId;
          tcWasCancelledAfter = isRecent && isSameUser;

          if (tcWasCancelledAfter) {
            console.log('[authUtils] TC was cancelled during handleTermsAndConditions, preventing SUCCESS dispatch');
            await AsyncStorage.removeItem('tc_cancelled');
            return;
          }
        }
      } catch (e) {
        console.error('[authUtils] Error checking cancellation flag after handleTermsAndConditions:', e);
      }

      // Double-check TC modal state after handleTermsAndConditions
      // But if terms were just accepted, allow proceeding
      const tcModalStateAfterTC = store.getState()?.modals?.tcModal;
      if (!termsJustAccepted && tcModalStateAfterTC?.userId && !tcModalStateAfterTC?.modalDisplayed && tcModalStateAfterTC?.accepted === false) {
        console.log('[authUtils] TC modal was declined after handleTermsAndConditions, preventing SUCCESS dispatch');
        return;
      }

      const amendedData = await handleUserData(res, notificationToken);

      // Verify amendedData has valid userData before dispatching SUCCESS
      if (!amendedData?.userData?.id) {
        console.error('[authUtils] Invalid user data in amendedData, preventing SUCCESS dispatch:', amendedData);
        handleError(dispatch, 'Authentication succeeded but user data is incomplete. Please try again.');
        return;
      }

      // Final check before dispatching SUCCESS
      // But if terms were just accepted, allow proceeding
      const tcModalStateBeforeSuccess = store.getState()?.modals?.tcModal;
      if (!termsJustAccepted && tcModalStateBeforeSuccess?.userId && !tcModalStateBeforeSuccess?.modalDisplayed && tcModalStateBeforeSuccess?.accepted === false) {
        console.log('[authUtils] TC modal was cancelled before SUCCESS, preventing dispatch');
        return;
      }

      console.log('[authUtils] Dispatching SUCCESS with user data');
      markRecentAuthSuccess();
      amendedData.rememberMe = true;
      amendedData.expireAt = computeSessionExpireAt(true);
      dispatch({ type: SUCCESS, data: amendedData });

      // Ensure userData exists before navigating
      if (amendedData?.userData?.id) {
        try {
          await persistAuthSession({
            rememberMe: true,
            userId: amendedData.userData.id,
            sessionData: amendedData,
          });
        } catch (storageError) {
          console.warn('Failed to save session to storage:', storageError);
        }

        // Initialize menu for web
        if (Platform.OS === 'web') {
          dispatch({ type: INITIALISE_MENU });
        }

        const isNewUser = computePostOAuthIsNewUser(amendedData);
        
        // Check TC modal state before navigating
        // But if terms were just accepted, proceed with navigation
        const tcModalVisible = store.getState()?.modals?.tcModal?.modalDisplayed;
        console.log('[authUtils] handleOAuthResponse - TC modal state:', tcModalVisible, 'termsJustAccepted:', termsJustAccepted);

        if (tcModalVisible && !termsJustAccepted) {
          console.log('[authUtils] TC modal visible and terms not just accepted, skipping direct navigation - will be handled by useEffect');
          return;
        }

        // Prevent LoginScreen fetchAndValidateSession from re-opening the global loading modal and
        // racing getSession/biometrics after we already have session + root replace (native loading loop).
        if (Platform.OS !== 'web') {
          dispatch({ type: SET_SKIP_NATIVE_LOGIN_SESSION_FETCH, payload: true });
          // OAuth skips fetchAndValidateSession — push cookie to Watch here or companion stays logged out.
          syncWatchSessionLater({
            userId: amendedData.userData.id,
            companyId: amendedData?.company?._id || amendedData?.company?.id || null,
            companyName:
              amendedData?.company?.company_name ||
              amendedData?.company?.copmany_name ||
              null,
            targetUserId:
              amendedData?.targetUser?._id || amendedData?.targetUser?.id || null,
          });
        }

        // Direct navigation using session from OAuth. On native, root navigationRef avoids
        // cases where useNavigation() from a button does not perform LoginStack.replace after WebBrowser.
        const navigationDelay = termsJustAccepted ? 300 : 0;

        setTimeout(() => {
          if (isNewUser) {
            console.log('[authUtils] [new user], starting onboarding');
            void trackSignupComplete({ method: providerName || 'oauth', source: 'oauth' });
            dispatchPostAuthEasyModeOnboarding(dispatch);
            console.log('[authUtils] Navigating to Dashboard/EasyMode');
            replaceLoginStackWithDashboard(navigation, true, amendedData);
          } else {
            console.log('[authUtils] Navigating to Dashboard/Welcome');
            replaceLoginStackWithDashboard(navigation, false, amendedData);
          }
        }, navigationDelay);
      } else {
        console.error('OAuth response missing userData.id:', amendedData);
        handleError(dispatch, 'Authentication succeeded but user data is incomplete. Please try again.');
      }
    }
  } catch (e) {
    handleError(dispatch, `Failed to authenticate ${providerName} Account, please try again`);
  } finally {
    dispatch({ type: HIDE_LOADING_MODAL });
  }
}

// Common user type interface
export interface OAuthUser {
  surname?: string;
  name?: string;
  id: string;
  email: string;
  oauthToken: string;
  type: string;
  notificationToken: string;
  device: {
    brand: string;
    model: string;
    os: string;
    osVersion: string;
  };
}

// Create OAuth user object
export function createOAuthUser(
  user: any,
  token: string,
  type: string,
  notificationToken: string
): OAuthUser {
  return {
    surname: user.family_name || user.surname,
    name: user.given_name || user.name,
    id: user.id,
    email: user.email,
    oauthToken: token,
    type,
    notificationToken,
    device: {
      brand: DeviceInfo.getBrand(),
      model: DeviceInfo.getModel(),
      os: DeviceInfo.getSystemName(),
      osVersion: DeviceInfo.getSystemVersion(),
    },
  };
}

// Common function to handle OAuth login flow
export async function handleOAuthLogin(
  user: OAuthUser,
  dispatch: any,
  navigation: any,
  notificationToken: string,
  providerName: string
) {
  try {
    dispatch({ type: SHOW_LOADING_MODAL });

    const res = await apis.handleOAuth(user);
    await handleOAuthResponse(res, dispatch, navigation, notificationToken, providerName);

  } catch (e) {
    handleError(dispatch, `Failed to authenticate ${providerName} Account, please try again`);
  }
}

// Common function to fetch user info from OAuth provider
export async function fetchUserInfoFromProvider(
  apiUrl: string,
  token: string,
  setUserInfo: (userInfo: any) => void,
  providerName: string
) {
  try {
    const apiResponse = await fetch(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    console.log(`${providerName} API Response:`, apiResponse);

    if (apiResponse.ok) {
      const user = await apiResponse.json();
      setUserInfo(user);
      console.log(`${providerName} User Info:`, user);
      return user;
    } else {
      throw new Error(`Failed to fetch user information from ${providerName}`);
    }
  } catch (error) {
    console.error(`Failed to fetch ${providerName} user info:`, error);
    throw error;
  }
}
